import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { getIsIncludeProductsChecked, getIsVariantWithIncludes } from "@thkStore/Store/Selectors/IncludeProductsSelectors";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const selectedProductId = state.pages.productDetails.selectedProductId;
    const product = selectedProductId ? getProductState(state, selectedProductId).value : undefined;
    return {
        isAuthenticated: state.context.session.isAuthenticated,
        clipboardItemCount: Object.keys((state as any).clipboard?.items ?? {}).length,
        isVariantWithIncludes: getIsVariantWithIncludes(product),
        includeProductsChecked: getIsIncludeProductsChecked(state, selectedProductId),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsVariantLayoutStyles {
    container?: GridContainerProps;
    clipboardGridItem?: GridItemProps;
    clipboardGridItemChild?: GridItemProps;
}

export const variantLayoutStyles: ProductDetailsVariantLayoutStyles = {
    container: {
        gap: 30,
        css: css`
            margin-top: 40px;
        `,
    },
    // Parent page (variant grid visible): clipboard sits beside the 71% grid on the right.
    // flex: 0 0 29% pins it to a fixed width so flexbox can't shrink it toward its content
    // size (GridContainer sets flex-shrink: 1 on every grid item, and the variant item's
    // flex-basis is 100%, so without this the clipboard collapses and resizes per selection).
    clipboardGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            width: 100%;

            @media (min-width: 1024px) {
                flex: 0 0 29% !important;
                width: 29% !important;
                max-width: 29% !important;
            }
        `,
    },
    // Variant child page (variant grid hidden, clipboard is the lone column): same fixed
    // width, nudged left of center so it sits under the Include Products checkbox.
    clipboardGridItemChild: {
        width: [12, 12, 4, 4, 4],
        css: css`
            width: 100%;

            @media (min-width: 1024px) {
                flex: 0 0 29% !important;
                width: 29% !important;
                max-width: 29% !important;
                margin-left: 25% !important;
                margin-right: auto !important;
            }
        `,
    },
};

const VariantGridItem = styled(GridItem)<{ $isAuthenticated: boolean }>`
    @media (min-width: 1024px) {
        width: ${({ $isAuthenticated }) => $isAuthenticated ? '71%' : '100%'} !important;
        max-width: ${({ $isAuthenticated }) => $isAuthenticated ? '71%' : '100%'} !important;
    }
`;

const styles = variantLayoutStyles;

const ProductDetailsVariantLayout: React.FC<Props> = ({ id, isAuthenticated, clipboardItemCount, isVariantWithIncludes, includeProductsChecked }) => {
    const showClipboard = isAuthenticated && (
        isVariantWithIncludes ? includeProductsChecked : clipboardItemCount > 0
    );

    const clipboardRef = React.useRef<HTMLDivElement>(null);
    const prevCheckedRef = React.useRef(includeProductsChecked);

    // When the user ticks the Include Products checkbox the clipboard appears below;
    // scroll it into view so they don't miss it. Only fire on the false -> true
    // transition (not on initial load when the box is already checked). Skip on mobile,
    // where the clipboard is a fixed bottom bar that's always visible and the in-flow
    // wrapper is just an empty placeholder.
    React.useEffect(() => {
        const isInFlowLayout = typeof window !== "undefined" && window.innerWidth >= 768;
        if (isInFlowLayout && isVariantWithIncludes && !prevCheckedRef.current && includeProductsChecked) {
            clipboardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        prevCheckedRef.current = includeProductsChecked;
    }, [isVariantWithIncludes, includeProductsChecked]);

    return (
        <GridContainer {...styles.container}>
            {!isVariantWithIncludes && (
                <VariantGridItem width={12} $isAuthenticated={showClipboard}>
                    {/* Area where the VariantChildrenGrid is inserted */}
                    <Zone contentId={id} zoneName="VariantGrid" />
                </VariantGridItem>
            )}
            {showClipboard && (
                <GridItem {...(isVariantWithIncludes ? styles.clipboardGridItemChild : styles.clipboardGridItem)}>
                    {/* Area where the ClipboardSummary is inserted. GridItem is a flex row,
                        so this scroll-target wrapper must fill the column width (width: 100%)
                        — otherwise it shrinks to the card's content and the card resizes with
                        the selection. */}
                    <div ref={clipboardRef} style={{ width: "100%" }}>
                        <Zone contentId={id} zoneName="ClipboardSummary" />
                    </div>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductDetailsVariantLayout),
    definition: {
        displayName: "Variant Grid Layout",
        group: "Product Details",
        icon: "layout",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
